import { getPermalinkEncodedFilterKeys } from '@/features/seo-listing/consts';
import type { ParsedListingFilters } from '@/features/seo-listing/types';

export interface ListingIdentityFilters {
  selectedFandoms: string[];
  selectedCharacters: string[];
  selectedTags: string[];
  selectedPairingCharacters: string[];
}

const IDENTITY_KEYS = [
  'selectedFandoms',
  'selectedCharacters',
  'selectedTags',
  'selectedPairingCharacters',
] as const;

const ENCODED_KEY_TO_IDENTITY = {
  fandoms: 'selectedFandoms',
  characters: 'selectedCharacters',
  tags: 'selectedTags',
  pairingCharacters: 'selectedPairingCharacters',
} as const;

/**
 * Diff path-encoded identity filters for popstate restore.
 * Query-synced facets are handled separately by syncStateFromUrl.
 */
export const buildPopstateIdentityUpdates = (
  pathname: string,
  snapshot: ListingIdentityFilters,
  current: ListingIdentityFilters,
): Partial<ListingIdentityFilters> => {
  const encodedKeys = getPermalinkEncodedFilterKeys(pathname);
  const updates: Partial<ListingIdentityFilters> = {};

  for (const encodedKey of encodedKeys) {
    const identityKey = ENCODED_KEY_TO_IDENTITY[encodedKey];
    const snapshotValue = snapshot[identityKey];
    const currentValue = current[identityKey];
    if (JSON.stringify(snapshotValue) !== JSON.stringify(currentValue)) {
      updates[identityKey] = snapshotValue;
    }
  }

  return updates;
};

export const listingIdentityFromParsedFilters = (
  filters: Pick<ParsedListingFilters, 'fandoms' | 'characters' | 'tags' | 'pairingCharacters'>,
): ListingIdentityFilters => ({
  selectedFandoms: filters.fandoms,
  selectedCharacters: filters.characters,
  selectedTags: filters.tags,
  selectedPairingCharacters: filters.pairingCharacters,
});

export const listingIdentityFromFilterState = (
  filters: ListingIdentityFilters,
): ListingIdentityFilters => ({
  selectedFandoms: filters.selectedFandoms,
  selectedCharacters: filters.selectedCharacters,
  selectedTags: filters.selectedTags,
  selectedPairingCharacters: filters.selectedPairingCharacters,
});

export const hasListingIdentityUpdates = (updates: Partial<ListingIdentityFilters>): boolean =>
  IDENTITY_KEYS.some((key) => updates[key] !== undefined);
